export const AB_API_HOSTS = {
	development: 'https://dev-api.aofl.com',
	local_development: 'https://dev-api.aofl.com',
	production: 'https://api.aofl.com',
	// Review apps talk to the dev backend, same as development (shared-constants.js's
	// getApiBaseUrl documents the same pattern). Missing this key sent `undefined` into
	// `${host}/ws/analytics/...`, producing a relative URL the browser resolved against
	// the current page path (observed on review as /abc/login/undefined/ws/analytics/...).
	review: 'https://dev-api.aofl.com',
	staging: 'https://api.aofl.com',
};

export const DEVICE_KEY_MAP = {
	acquisition_source: 'acquisitionSource',
	device_id: 'deviceId',
	// Deprecated incoming device attribute name; kept so existing web targeting still maps to the store field `localeCode`.
	localeCode: 'localeCode',
	// AB admin / app attribute (`$device.locale`); maps to the same store field so web experiments can match app targeting.
	locale: 'localeCode',
	is_first_time_visit: 'isFirstTimeVisit',
	app_version: 'appVersion',
	country_code: 'viewerCountry',
	is_mobile_viewer: 'isMobileViewer',
	is_desktop_viewer: 'isDesktopViewer',
	is_webview: 'inApp',
	app_store: 'appStore',
	browser_platform: 'browserPlatform',
	// is_ios_viewer: 'isIosViewer',
	is_link_from_app: 'isLinkFromApp',
	is_link_from_app_platform: 'isLinkFromAppPlatform',
	is_link_from_email: 'isLinkFromEmail',
	is_automated_testing: 'isAutomatedTesting',
};

export const EXPERIMENT_ATTRIBUTION_TYPES = {
	DEVICE: 'device',
	MASTER: 'master',
};

/**
 * @typedef {object} ActiveExperimentDefinition
 * @property {string} [attributionType]
 * @property {string} experimentKey
 * @property {boolean} [hotjar] `true` includes the experiment in Hotjar capture. Omitted experiments are excluded.
 * @property {Record<string, string>} [offerHashByVariant]
 * @property {boolean} [pinOnAccountCreate]
 * @property {Record<string, string>} variants
 */

/** @type {Record<string, ActiveExperimentDefinition>} */
export const ACTIVE_EXPERIMENTS = {
	ABCM2_THIRD_PARTY_AUTH_WEB: {
		attributionType: EXPERIMENT_ATTRIBUTION_TYPES.DEVICE,
		experimentKey: 'abcm2-third-party-auth-web',
		variants: {
			test: 'test',
			control: 'default',
		},
	},
	ABCM2_THIRD_PARTY_AUTH_APP: {
		attributionType: EXPERIMENT_ATTRIBUTION_TYPES.DEVICE,
		experimentKey: 'abcm2-third-party-auth-app',
		variants: {
			test: 'test',
			control: 'default',
		},
	},
	ABCM2_MAX_TIER_OFFER: {
		attributionType: EXPERIMENT_ATTRIBUTION_TYPES.MASTER,
		experimentKey: 'subscription-tier-max',
		variants: {
			test: 'test-a',
			test_b: 'test-b',
			control: 'default',
		},
	},
	// EXPERIMENT_CLEANUP: nmhp-labor-day-badge (concluded — no ab_admin run; keep for NMHP prefetch until code cleanup)
	ABCM2_NMHP_LABOR_DAY_BADGE: {
		attributionType: EXPERIMENT_ATTRIBUTION_TYPES.DEVICE,
		experimentKey: 'nmhp-labor-day-badge',
		pinOnAccountCreate: false,
		variants: {
			test: 'test',
			control: 'default',
		},
	},
	// EXPERIMENT_CLEANUP: nmhp-sticky-header
	ABCM2_NMHP_STICKY_HEADER: {
		attributionType: EXPERIMENT_ATTRIBUTION_TYPES.DEVICE,
		experimentKey: 'nmhp-sticky-header',
		hotjar: true,
		variants: {
			test: 'test',
			control: 'default',
		},
	},
	// EXPERIMENT_CLEANUP: nmhp-chatbot (abcm2-nmhp-chatbot)
	ABCM2_NMHP_CHATBOT: {
		attributionType: EXPERIMENT_ATTRIBUTION_TYPES.DEVICE,
		experimentKey: 'abcm2-nmhp-chatbot',
		hotjar: true,
		variants: {
			test: 'test',
			control: 'default',
		},
	},
	ABCM2_AA_OFFER_REDESIGN: {
		attributionType: EXPERIMENT_ATTRIBUTION_TYPES.MASTER,
		experimentKey: 'browser-academy-upsell-redesign',
		variants: {
			test: 'test',
			control: 'default',
		},
	},
	ABCM2_PRE_PAID_CARDS_V4: {
		attributionType: EXPERIMENT_ATTRIBUTION_TYPES.DEVICE,
		experimentKey: 'decline-prepaid-cards-v4',
		variants: {
			test: 'test',
			control: 'default',
		},
	},
	ABCM2_ANNUAL_ASSESSMENTS_ORIGINALLY_99_99_COPY: {
		attributionType: EXPERIMENT_ATTRIBUTION_TYPES.MASTER,
		experimentKey: 'annual-assessments-originally-99-99-copy',
		variants: {
			test: 'test',
			control: 'default',
		},
	},
	ABCM2_ROKT: {
		attributionType: EXPERIMENT_ATTRIBUTION_TYPES.DEVICE,
		experimentKey: 'browser-so-rokt-create-account-product-info',
		offerHashByVariant: {
			'test a': '2kt9wrn',
			'test b': '2kt9vvn',
			'default': '123br4nd',
		},
		variants: {
			test_a: 'test a',
			test_b: 'test b',
			control: 'default',
		},
	},
	ABCM2_SIGNIFYD_V2: {
		attributionType: EXPERIMENT_ATTRIBUTION_TYPES.DEVICE,
		experimentKey: 'signifyd-v2',
		variants: {
			test: 'test', // Cohort B — payment routed through Signifyd pre-auth
			control: 'default', // Cohort A — BAU, direct to processor
		},
	},
	ABCM2_SUB_PAGE_PAYMENT_DISPLAY_V2: {
		attributionType: EXPERIMENT_ATTRIBUTION_TYPES.DEVICE,
		experimentKey: 'abcm2-sub-page-payment-display-v2',
		variants: {
			testA: 'test-a',
			testB: 'test-b',
			control: 'default',
		},
	},
	ABCM2_REMOVE_SEMIANNUAL_POPUPS: {
		attributionType: EXPERIMENT_ATTRIBUTION_TYPES.MASTER,
		experimentKey: 'abcm2-remove-semiannual-popups',
		variants: {
			test: 'test',
			control: 'default',
		},
	},
	ABCM2_MERCH_UPSELL_READING_MATH_PACK: {
		attributionType: EXPERIMENT_ATTRIBUTION_TYPES.MASTER,
		experimentKey: 'abcm2-merch-upsell-reading-math-pack',
		variants: {
			test: 'test',
			control: 'default',
		},
	},
};

export const PROFILE = 'abcmouse';

export const EXPERIMENT_KEYS = [ACTIVE_EXPERIMENTS.ABCM2_MAX_TIER_OFFER.experimentKey];

export const DATA_TYPES = {
	DEVICE: 'device',
	USER: 'user',
	SESSION: 'session',
	MASTER: 'master',
};

export const DATA_KEYS = {
	REACTIVATED_USER: 'is_reactivated_user',
	SPECIAL_OFFER_USER: 'is_special_offer_user',
	SUBSCRIPTION_STATUS: 'subscription_status',
	USER_SUBSCRIPTION_TYPE: 'user_subscription_type',
};
